import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useSeo } from "@/lib/seo";
import { BRAND_SIGNATURE, SIGNATURE_PILLARS } from "@/lib/brand";

export function InsightsPage() {
  const { lang, L, t } = useLang();
  const [insights, setInsights] = useState([]);
  useSeo({ title: t("insights_title"), description: t("nav_insights"), lang });

  useEffect(() => {
    api.get("/insights").then(({ data }) => setInsights(data)).catch(() => {});
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
      <span className="text-xs uppercase tracking-[0.3em] text-champagne">{t("nav_insights")}</span>
      <h1 className="mt-3 font-serif text-4xl tracking-tight text-anthracite sm:text-5xl">{t("insights_title")}</h1>

      {insights.length === 0 ? (
        <div className="mt-12 border border-dashed border-tertiary bg-white py-20 text-center text-anthracite/50">
          {lang === "fr" ? "Analyses à venir." : "Insights coming soon."}
        </div>
      ) : (
        <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {insights.map((p, i) => (
            <Link key={p.id} to={`/insights/${p.slug}`} className="group animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
              {p.cover_image && (
                <div className="mb-4 aspect-[16/10] overflow-hidden bg-sand">
                  <img src={p.cover_image} alt={L(p, "title")} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                </div>
              )}
              <h3 className="font-serif text-xl text-anthracite group-hover:text-champagne">{L(p, "title")}</h3>
              <p className="mt-2 text-sm text-anthracite/60 line-clamp-2">{L(p, "excerpt")}</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function InsightDetail() {
  const { slug } = useParams();
  const { L, lang } = useLang();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/insights/${slug}`).then(({ data }) => setPost(data)).catch(() => setError(true));
  }, [slug]);

  useSeo({
    title: post ? L(post, "title") : "",
    description: post ? L(post, "excerpt") : "",
    lang,
    image: post?.cover_image,
    type: "article",
  });

  if (error) return <div className="mx-auto max-w-3xl px-6 py-24 text-center text-anthracite/60">404</div>;
  if (!post) return <div className="mx-auto max-w-3xl px-6 py-24 text-center text-anthracite/40">…</div>;

  return (
    <article className="mx-auto max-w-3xl px-6 py-14 lg:px-10">
      <Link to="/insights" className="mb-8 inline-flex items-center gap-2 text-sm text-anthracite/60 hover:text-champagne">
        <ArrowLeft className="h-4 w-4" /> {lang === "fr" ? "Toutes les analyses" : "All insights"}
      </Link>
      <h1 className="mb-6 font-serif text-4xl leading-tight tracking-tight text-anthracite sm:text-5xl">{L(post, "title")}</h1>
      {post.published_at && (
        <p className="mb-8 text-sm text-anthracite/50">{new Date(post.published_at).toLocaleDateString(lang)}</p>
      )}
      {post.cover_image && (
        <div className="mb-10 aspect-[16/9] overflow-hidden bg-sand">
          <img src={post.cover_image} alt={L(post, "title")} className="h-full w-full object-cover" />
        </div>
      )}
      <div className="whitespace-pre-line text-lg leading-relaxed text-anthracite/80">{L(post, "body")}</div>
    </article>
  );
}

export function AboutPage() {
  const { lang, t } = useLang();
  const [stats, setStats] = useState(null);
  useSeo({
    title: t("nav_about"),
    description: lang === "fr"
      ? "RSTI identifie, vérifie, structure et connecte opportunités, projets, investisseurs, partenaires et marchés."
      : "RSTI identifies, verifies, structures and connects opportunities, projects, investors, partners and markets.",
    lang,
  });
  useEffect(() => {
    api.get("/stats/public").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div className="mx-auto max-w-4xl px-6 py-16 lg:px-10">
      <span className="text-xs uppercase tracking-[0.3em] text-champagne">{t("nav_about")}</span>
      <h1 className="mt-3 font-serif text-4xl leading-tight tracking-tight text-anthracite sm:text-5xl">
        {lang === "fr" ? "RSTI Global · Reveal, Structure, Transformer, Impact" : "RSTI Global · Reveal, Structure, Transform, Impact"}
      </h1>
      <p className="mt-6 text-lg leading-relaxed text-anthracite/70">
        {lang === "fr"
          ? "RSTI identifie, vérifie, structure et connecte des opportunités, projets, investisseurs, financements, partenaires, fournisseurs, producteurs, distributeurs, experts et marchés. Une plateforme internationale ancrée en Afrique, sélective dans son expertise."
          : "RSTI identifies, verifies, structures and connects opportunities, projects, investors, funding, partners, suppliers, producers, distributors, experts and markets. An international platform anchored in Africa, selective in expertise."}
      </p>
      <div className="mt-12 border border-tertiary bg-white p-8" data-testid="about-signature">
        <p className="text-xs uppercase tracking-[0.3em] text-champagne">{lang === "fr" ? "Notre signature" : "Our signature"}</p>
        <p className="mt-3 font-serif text-2xl text-anthracite">{BRAND_SIGNATURE}</p>
        <dl className="mt-6 grid gap-4 sm:grid-cols-2">
          {SIGNATURE_PILLARS.map((p) => (
            <div key={p.key} className="border-l-2 border-champagne pl-4">
              <dt className="font-serif text-lg text-anthracite">{p.key}</dt>
              <dd className="text-sm text-anthracite/60">{lang === "fr" ? p.fr : p.en}</dd>
            </div>
          ))}
        </dl>
      </div>
      {stats && (
        <div className="mt-12 grid grid-cols-2 gap-px overflow-hidden border border-tertiary bg-tertiary sm:grid-cols-4">
          {[
            { v: stats.listings, l: lang === "fr" ? "Fiches publiées" : "Published" },
            { v: stats.verified, l: t("verified") },
            { v: stats.experts, l: lang === "fr" ? "Experts" : "Experts" },
            { v: stats.categories, l: lang === "fr" ? "Domaines" : "Domains" },
          ].map((s, i) => (
            <div key={i} className="bg-white p-6 text-center">
              <p className="font-serif text-3xl text-champagne">{s.v}</p>
              <p className="mt-1 text-xs uppercase tracking-[0.15em] text-anthracite/50">{s.l}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
